import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Toast } from 'antd-mobile';
import { useTranslation } from '@/i18n';
import './index.scss';

const AccountDelete = () => {
  const history = useHistory();
  const t = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [isAgreed, setIsAgreed] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 检查登录状态
  useEffect(() => {
    const checkLoginStatus = () => {
      // 兼容现有登录存储键名
      const token =
        localStorage.getItem('userToken') ||
        sessionStorage.getItem('userToken') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('token');
      const user =
        localStorage.getItem('driverDetail') ||
        sessionStorage.getItem('driverDetail') ||
        localStorage.getItem('userInfo') ||
        sessionStorage.getItem('userInfo');

      if (!token) {
        Toast.show({
          content: '请先登录',
          duration: 2000,
        });
        // 跳转到登录页
        setTimeout(() => {
          history.replace('/login?redirect=/accountDelete');
        }, 2000);
        return false;
      }

      if (user) {
        try {
          setUserInfo(JSON.parse(user));
        } catch (error) {
          console.error('解析用户信息失败:', error);
        }
      }
      return true;
    };

    checkLoginStatus();
  }, [history]);

  // 设置页面标题
  useEffect(() => {
    document.title = '账号注销';
    // 设置页面标题（如果在 WebView 中）
    if (typeof window?.savePicture?.setTitle === 'function') {
      window?.savePicture?.setTitle('账号注销');
    }
  }, []);

  // 处理注销按钮点击
  const handleDeleteAccount = () => {
    if (!isAgreed) {
      Toast.show({
        content: '请先阅读并同意注销协议',
        duration: 2000,
      });
      return;
    }
    setShowConfirmModal(true);
  };

  // 确认注销
  const confirmDelete = () => {
    // 这里不调用接口，只做本地处理
    Toast.loading('正在处理...', 2);

    setTimeout(() => {
      // 清除本地存储的用户信息
      localStorage.removeItem('userToken');
      localStorage.removeItem('driverDetail');
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      sessionStorage.removeItem('userToken');
      sessionStorage.removeItem('driverDetail');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('userInfo');

      Toast.success('账号注销成功', 2);

      setTimeout(() => {
        // 跳转到登录页
        history.replace('/login');
      }, 2000);
    }, 2000);
  };

  // 取消注销
  const cancelDelete = () => {
    setShowConfirmModal(false);
  };

  const displayName = userInfo?.driverName || '未获取姓名';
  const displayPhone = userInfo?.driverPhone || '未获取手机号';
  const displayPlate = userInfo?.vehicleNo || '-';
  const displayFleet = userInfo?.fleetName || '-';
  const displayStatus = userInfo ? '已登录' : '未获取';
  const avatarText = displayName?.slice(0, 1) || '账';

  return (
    <div className="account-delete-container">
      {/* 主内容区 */}
      <div className="delete-content">
        {/* 账号概要 */}
        <div className="account-summary">
          <div className="avatar">{avatarText}</div>
          <div className="info">
            <div className="name-row">
              <span className="name">{displayName}</span>
              <span className="status-tag">{displayStatus}</span>
            </div>
            <div className="phone">{displayPhone}</div>
            <div className="meta">
              <span>车牌号：{displayPlate}</span>
              <span>车队：{displayFleet}</span>
            </div>
          </div>
        </div>

        {/* 警告提示 */}
        <div className="warning-section">
          <div className="warning-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
          </div>
          <h2 className="warning-title">注销账号前请仔细阅读</h2>
        </div>

        {/* 注销说明 */}
        <div className="info-section">
          <div className="info-card">
            <h3 className="card-title">注销后您将失去以下权益：</h3>
            <ul className="info-list">
              <li>
                <span className="bullet">•</span>
                <span className="text">账号信息将被永久删除，无法恢复</span>
              </li>
              <li>
                <span className="bullet">•</span>
                <span className="text">账号内的余额、积分等资产将被清空</span>
              </li>
              <li>
                <span className="bullet">•</span>
                <span className="text">所有订单历史记录将被删除</span>
              </li>
              <li>
                <span className="bullet">•</span>
                <span className="text">已绑定的手机号将被解绑</span>
              </li>
              <li>
                <span className="bullet">•</span>
                <span className="text">无法再使用该账号登录和使用服务</span>
              </li>
            </ul>
          </div>

          <div className="info-card">
            <h3 className="card-title">注销条件：</h3>
            <ul className="info-list">
              <li>
                <span className="bullet">•</span>
                <span className="text">账号内无未完成的订单</span>
              </li>
              <li>
                <span className="bullet">•</span>
                <span className="text">账号内无未提现的余额</span>
              </li>
              <li>
                <span className="bullet">•</span>
                <span className="text">账号无未处理的纠纷或投诉</span>
              </li>
              <li>
                <span className="bullet">•</span>
                <span className="text">账号状态正常，未被冻结或限制</span>
              </li>
            </ul>
          </div>

          <div className="info-card highlight">
            <div className="highlight-content">
              <svg className="highlight-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              <p className="highlight-text">
                注销申请提交后，账号将立即失效。请确认您已了解上述内容并做好相关准备。
              </p>
            </div>
          </div>
        </div>

        {/* 协议勾选 */}
        {/* <div className="agreement-section">
          <label className="checkbox-wrapper">
            <input
              type="checkbox"
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
            />
            <span className="checkbox-custom">
              {isAgreed && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              )}
            </span>
            <span className="agreement-text">
              我已阅读并同意
              <span className="link">《账号注销协议》</span>
            </span>
          </label>
        </div> */}

        {/* 注销按钮 */}
        <div className="action-section">
          <button 
            className={`delete-btn ${isAgreed ? 'active' : 'disabled'}`}
            onClick={handleDeleteAccount}
            disabled={!isAgreed}
          >
            申请注销
          </button>
          <p className="hint-text">注销后将无法恢复，请谨慎操作</p>
        </div>
      </div>

      {/* 确认弹窗 */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <svg className="modal-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
            </div>
            <h3 className="modal-title">确认注销账号？</h3>
            <p className="modal-desc">
              注销后账号信息将被永久删除，且无法恢复。请再次确认是否要注销账号。
            </p>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={cancelDelete}>
                我再想想
              </button>
              <button className="modal-btn confirm" onClick={confirmDelete}>
                确认注销
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDelete;
